import React, { useState } from 'react';
import { PhoneOutgoing, Mail, MessageSquare, CheckCircle2, History } from 'lucide-react';
import { HiViewGrid } from 'react-icons/hi';
import type { Contact } from '../../../../types/contact.types';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { regenerateFollowUpPlan } from '../../../../api/remiApi';
import { formatDate } from '../../../../utils/dateTime';
import { getCallOutcomeStyle } from '../../../../utils/badgeStyles';

interface FutureOutreachPlanCardProps {
  contact?: Contact | null;
}

type Channel = 'sms' | 'call' | 'email' | 'other';

// Channel colors + icons mirror the Communication so far feed (contacts.css)
// so plan steps and activity items share one channel system, light + dark.
const CHANNEL_META: Record<
  Channel,
  { label: string; bgClass: string; colorClass: string; icon: React.ReactNode }
> = {
  sms: {
    label: 'SMS',
    bgClass: 'call-icon-inbound',
    colorClass: 'call-color-inbound',
    icon: <MessageSquare className="w-3 h-3" />,
  },
  call: {
    label: 'Call',
    bgClass: 'call-icon-outbound',
    colorClass: 'call-color-outbound',
    icon: <PhoneOutgoing className="w-3 h-3" />,
  },
  email: {
    label: 'Email',
    bgClass: 'ealert-icon-bg',
    colorClass: 'ealert-color',
    icon: <Mail className="w-3 h-3" />,
  },
  other: {
    label: 'Touch',
    bgClass: 'bg-amber-100 dark:bg-amber-500/20',
    colorClass: 'text-amber-700 dark:text-amber-300',
    icon: <CheckCircle2 className="w-3 h-3" />,
  },
};

function toChannel(raw: string | null): Channel {
  switch ((raw || '').toLowerCase()) {
    case 'sms':
    case 'text':
      return 'sms';
    case 'call':
    case 'voice':
    case 'phone':
      return 'call';
    case 'email':
      return 'email';
    default:
      return 'other';
  }
}

// Soft amber node — subtle, single accent for every step on the timeline.
const STEP_NODE = 'bg-amber-200 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300';

const INTERVAL_LABEL: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  'bi-weekly': 'Bi-weekly',
  biweekly: 'Bi-weekly',
};

// SPEC-2023: resolvedOutcome ships the raw last-call outcome, and the real data
// is messy — mixed casing ("Conversation"/"conversation"), spaces vs underscores
// ("No Contact"/"No_Contact"), plus a few values outside the documented set. Both
// helpers collapse that noise: a normalized key for the badge-color lookup, and a
// Title Case label for display. Format-only — the meaning is never remapped.
function normalizeOutcomeKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function humanizeLabel(raw: string): string {
  return raw
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Canonical strategy id → display name. Falls back to humanizeLabel for ids not
// listed here; a null/empty strategy id renders nothing (SPEC-1976 sync gap).
const STRATEGY_LABEL: Record<string, string> = {
  speed_to_lead: 'Speed-to-Lead',
  convert: 'Convert',
  revive: 'Revive',
  engage: 'Engage',
  nurture: 'Nurture',
  call_back: 'Call Back',
  flywheel: 'Flywheel',
};

function strategyLabel(raw: string): string {
  return STRATEGY_LABEL[normalizeOutcomeKey(raw)] ?? humanizeLabel(raw);
}

// SPEC-1710: renders Contact.followUpPlan (server-built CadenceSteps). Regenerate
// POSTs the REST trigger; the planner runs async, so new steps land on a later read.
export const FutureOutreachPlanCard: React.FC<FutureOutreachPlanCardProps> = ({ contact }) => {
  const organization = useOrganizationStore((s) => s.organization);
  const assistantName = organization?.assistant_name || 'Remi';
  const timezone = organization?.timezone;
  const contactId = contact?.id;
  const firstName = contact?.firstName?.trim();
  // Possessive used in the subtitle: prefer the contact's first name when
  // we have one ("Riley's"), fall back to a generic phrase otherwise.
  const contactPossessive = firstName ? `${firstName}'s` : "the contact's";

  const [regenerating, setRegenerating] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = contact?.followUpPlan?.steps ?? [];

  // SPEC-2023: the backend fills these only when the plan has no upcoming steps —
  // the contact's last recorded call outcome. Rendered purely as a "what happened
  // last" label so the empty plan isn't an ambiguous blank. Not a handoff signal.
  const resolvedOutcome = contact?.followUpPlan?.resolvedOutcome ?? null;
  const resolvedStrategyId = contact?.followUpPlan?.resolvedStrategyId ?? null;
  const resolvedOutcomeAt = contact?.followUpPlan?.resolvedOutcomeAt ?? null;
  // Kept around (prefixed so unused-vars lets it through) so re-enabling the
  // commented-out Regenerate button is a one-line revert.
  const _handleRegenerate = async () => {
    if (regenerating || !contactId) return;
    setRegenerating(true);
    setError(null);
    setQueued(false);
    try {
      await regenerateFollowUpPlan(contactId);
      setQueued(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start regeneration.');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <section className="flex flex-col gap-5">
      {/* Head: gradient icon + title/sub + Regenerate button */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="btn-ai-gradient inline-flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0">
            <HiViewGrid className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] font-bold tracking-tight text-foreground leading-tight">
              Future outreach plan
            </div>
            <div className="text-[11px] lg:text-[11px] xl:text-xs 2xl:text-[13px] text-foreground/80 mt-0.5 leading-snug">
              What {assistantName} will do next, based on {contactPossessive} activities,
              conversations, and {assistantName}&apos;s memory.
            </div>
          </div>
        </div>
        {/* Regenerate button hidden — re-enable once the planner endpoint is
            stable enough to expose. The state + handler stay in place so the
            UI flips back on instantly.
        <Button
          variant="outline"
          size="sm"
          onClick={handleRegenerate}
          disabled={regenerating || !contactId}
          className="flex-shrink-0 gap-1.5"
          title={regenerating ? 'Regenerating…' : `Regenerate with ${assistantName}`}
        >
          {regenerating ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span className="hidden sm:inline">Regenerating…</span>
            </>
          ) : (
            <>
              <RotateCw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Regenerate with {assistantName}</span>
              <span className="sm:hidden">Regenerate</span>
            </>
          )}
        </Button>
        */}
      </div>

      {queued && (
        <p className="text-[11.5px] leading-relaxed text-amber-700 dark:text-amber-300">
          {assistantName} is rebuilding the plan — it&apos;ll update here shortly.
        </p>
      )}
      {error && (
        <p className="text-[11.5px] leading-relaxed text-red-600 dark:text-red-400">{error}</p>
      )}

      {(() => {
        // Timeline is built straight from the server plan steps — cadence
        // touches plus the backend's source="ealert" recurring property-alert
        // occurrences — sorted chronologically by `when`. Undated steps sink
        // to the top so they stay visible behind dated ones.
        const isHydrating = !contact;
        const parseISO = (raw: string | null): number | null => {
          if (!raw) return null;
          if (!/^\d{4}-\d{2}-\d{2}T/.test(raw)) return null;
          const ms = new Date(raw).getTime();
          return Number.isFinite(ms) ? ms : null;
        };
        const items = steps
          .map((step) => ({ step, whenMs: parseISO(step.when) }))
          .sort((a, b) => {
            if (a.whenMs == null && b.whenMs == null) return 0;
            if (a.whenMs == null) return -1;
            if (b.whenMs == null) return 1;
            return a.whenMs - b.whenMs;
          });
        if (items.length === 0) {
          if (isHydrating) {
            return (
              <div className="flex flex-col gap-3 animate-pulse" aria-hidden>
                <div className="grid grid-cols-[24px_1fr] gap-3">
                  <div className="w-6 h-6 rounded-full bg-foreground/10" />
                  <div className="flex flex-col gap-1.5">
                    <div className="h-3 w-24 rounded bg-foreground/10" />
                    <div className="h-3 w-48 rounded bg-foreground/10" />
                  </div>
                </div>
              </div>
            );
          }
          // SPEC-2023: no upcoming touch, but the backend recorded how this
          // contact was last handled — show that as a plain "what happened
          // last" label instead of a bare blank. This is not a handoff signal.
          if (resolvedOutcome) {
            const outcomeStyle = getCallOutcomeStyle(normalizeOutcomeKey(resolvedOutcome));
            const outcomeLabel = humanizeLabel(resolvedOutcome);
            const stratLabel = resolvedStrategyId ? strategyLabel(resolvedStrategyId) : null;
            const resolvedWhen =
              resolvedOutcomeAt && /^\d{4}-\d{2}-\d{2}T/.test(resolvedOutcomeAt)
                ? formatDate(resolvedOutcomeAt, timezone, 'MMM D, YYYY h:mm A z')
                : null;
            return (
              <div className="min-w-0 flex flex-col gap-4">
                <p className="text-[12.5px] leading-relaxed text-muted-foreground italic">
                  No outreach is scheduled for this contact.
                </p>
                <div className="flex flex-col gap-1.5 border-t border-foreground/10 pt-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <History className="w-3.5 h-3.5" />
                    What happened last
                  </div>
                  <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap text-[11px] xl:text-xs">
                    <span
                      className={`inline-flex items-center font-semibold px-2 py-0.5 rounded-full ${outcomeStyle.background} ${outcomeStyle.text}`}
                    >
                      {outcomeLabel}
                    </span>
                    {stratLabel && (
                      <span className="text-muted-foreground">
                        Strategy:{' '}
                        <span className="font-medium text-foreground/80">{stratLabel}</span>
                      </span>
                    )}
                    {resolvedWhen && (
                      <span className="text-foreground/80 font-medium tabular-nums">
                        {resolvedWhen}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] lg:text-[11px] xl:text-xs 2xl:text-[13px] text-muted-foreground leading-snug">
                    {assistantName} last reached this contact and classified the outcome as
                    {' '}&ldquo;{outcomeLabel}&rdquo;
                    {stratLabel ? ` via the ${stratLabel} strategy` : ''}.
                  </div>
                </div>
              </div>
            );
          }
          return (
            <p className="text-[12.5px] leading-relaxed text-muted-foreground italic">
              No outreach is scheduled for this contact.
            </p>
          );
        }
        return (
          <ol className="flex flex-col list-none p-0 m-0">
            {items.map(({ step: s }, idx) => {
              const isLast = idx === items.length - 1;
              const displayIndex = idx + 1;
              const nodeContent = (
                <div
                  className={`relative z-[1] w-6 h-6 rounded-full ${STEP_NODE} flex items-center justify-center text-[11px] font-bold tabular-nums ring-2 ring-white dark:ring-card`}
                >
                  {displayIndex}
                </div>
              );
              const connector = !isLast && (
                <span
                  aria-hidden
                  className="absolute left-[11px] top-6 bottom-0 w-px bg-foreground/15 dark:bg-foreground/20"
                />
              );
              // eAlert steps carry their cadence on `ealertInterval`. Badge
              // them as "eAlert" and surface the interval (Daily / Weekly /
              // Bi-weekly) as a small badge next to the date so the row reads
              // as a recurring alert without rewriting the title.
              const isEAlert = s.source === 'ealert';
              const intervalLabel = s.ealertInterval
                ? (INTERVAL_LABEL[s.ealertInterval.toLowerCase()] ?? s.ealertInterval)
                : null;
              const ch = isEAlert
                ? {
                    label: intervalLabel ? `${intervalLabel} eAlert` : 'eAlert',
                    bgClass: 'ealert-icon-bg',
                    colorClass: 'ealert-color',
                    icon: <Mail className="w-3 h-3" />,
                  }
                : CHANNEL_META[toChannel(s.channel)];
              return (
                <li
                  key={s.stepId}
                  className="relative grid grid-cols-[24px_1fr] gap-3 pb-5 last:pb-0"
                >
                  {connector}
                  {nodeContent}
                  <div className="min-w-0 pt-0.5">
                    <div className="flex items-center gap-1.5 flex-wrap text-[11px] xl:text-xs mb-1.5">
                      <span
                        className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded-full ${ch.bgClass} ${ch.colorClass}`}
                      >
                        {ch.icon}
                        {ch.label}
                      </span>
                      {s.when && (
                        <span className="text-foreground/80 font-medium tabular-nums">
                          {/^\d{4}-\d{2}-\d{2}T/.test(s.when)
                            ? formatDate(s.when, timezone, 'MMM D, YYYY h:mm A z')
                            : s.when}
                        </span>
                      )}
                    </div>
                    {s.title && (
                      <div className="text-[13px] font-semibold text-foreground tracking-tight mb-0.5">
                        {s.title}
                      </div>
                    )}
                    {s.purpose && (
                      <div className="text-[11px] lg:text-[11px] xl:text-xs 2xl:text-[13px] text-muted-foreground leading-snug">
                        {s.purpose}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        );
      })()}
    </section>
  );
};
